import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import Select from 'react-select';
import CalendarPicker from '../util/CalendarPicker';
import SelectUsers from '../docsComponents/SelectUsers';
import { fxSelectStyles } from '../modal/AddExpenses';


interface InsuranceFormProps {
  insuranceId: number | null;
  onClose: () => void;
  onSaved: () => void;
  baseUrl: string;
}

interface CarOption {
  value: number;
  label: string;
}

const InsuranceForm: React.FC<InsuranceFormProps> = ({
  insuranceId,
  onClose,
  onSaved,
  baseUrl,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = insuranceId != null;

  const [provider, setProvider] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [anyDriver, setAnyDriver] = useState(false);
  const [carIds, setCarIds] = useState<number[]>([]);
  const [driverIds, setDriverIds] = useState<number[]>([]);
  const [carOptions, setCarOptions] = useState<CarOption[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [docUrl, setDocUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const carsRes = await axios.get('cars');
      setCarOptions(
        (carsRes.data || []).map((c: any) => ({
          value: c.id,
          label: `${c.brand} ${c.model} (${c.plate_number})`,
        })),
      );
    } catch {
      /* interceptor */
    }
    if (insuranceId == null) return;
    try {
      const { data } = await axios.get(`finance-insurance/${insuranceId}`);
      if (!data) return;
      setProvider(data.provider || '');
      setPolicyNumber(data.policy_number || '');
      setExpiry(data.expiry_date ? new Date(data.expiry_date) : null);
      setAnyDriver(!!data.any_driver);
      setCarIds(Array.isArray(data.car_ids) ? data.car_ids : []);
      setDriverIds(Array.isArray(data.driver_ids) ? data.driver_ids : []);
      setDocUrl(data.document_url || '');
    } catch {
      /* interceptor */
    }
  }, [insuranceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!provider.trim() || !policyNumber.trim()) {
      enqueueSnackbar(t('FillRequiredFields') || 'Fill required fields', {
        variant: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        provider: provider.trim(),
        policy_number: policyNumber.trim(),
        expiry_date: expiry,
        any_driver: anyDriver,
        car_ids: carIds,
        driver_ids: anyDriver ? [] : driverIds,
      };

      let savedId = insuranceId;
      if (isEdit) {
        await axios.put(`finance-insurance/${insuranceId}`, payload);
      } else {
        const { data } = await axios.post('finance-insurance/add', payload);
        savedId = data?.id;
      }

      if (file && savedId) {
        const fd = new FormData();
        fd.append('insurance_document', file);
        await axios.post(`finance-insurance/upload/${savedId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      enqueueSnackbar(t('Saved') || 'Saved', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || t('ErrorSaving') || 'Error saving',
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedCars = carOptions.filter((o) => carIds.includes(o.value));

  return (
    <div className="fx-modal" role="presentation">
      {/* Header */}
      <div className="fx-head">
        <div className="fx-head-text">
          <div className="fx-head-title">
            {isEdit ? t('EditPolicy') || 'Edit policy' : t('AddPolicy') || 'Add policy'}
          </div>
        </div>
        <div className="fx-close" onClick={onClose}><CloseIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className="fx-body">
        <div className="fx-field">
          <input
            title={t('Provider') as string}
            className="fx-input"
            value={provider}
            placeholder={`${t('Provider')} *`}
            onChange={(e) => setProvider(e.target.value)}
          />
        </div>

        <div className="fx-row2">
          <div className="fx-field">
            <input
              title={t('PolicyNumber') as string}
              className="fx-input"
              value={policyNumber}
              placeholder={`${t('PolicyNumber')} *`}
              onChange={(e) => setPolicyNumber(e.target.value)}
            />
          </div>
          <div className="fx-field">
            <CalendarPicker
              mode="single"
              variant="bare"
              className="fx-input"
              value={expiry}
              onChange={(date: any) => setExpiry(date)}
              displayFormat="DD/MM/YYYY"
              clearable
            />
          </div>
        </div>

        <div className="fx-field">
          <Select
            isMulti
            isClearable
            closeMenuOnSelect={false}
            placeholder={t('CoveredVehicles') || 'Covered vehicles'}
            options={carOptions}
            value={selectedCars}
            onChange={(opts) => setCarIds((opts as CarOption[]).map((o) => o.value))}
            classNamePrefix="app-rs"
            styles={fxSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          />
        </div>

        {/* Drivers */}
        <div className="fx-section">{t('Drivers') || 'Drivers'}</div>
        <div className="fx-switch-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          <label className="flex items-center gap-[8px] text-[13px] cursor-pointer">
            <input
              type="checkbox"
              checked={anyDriver}
              onChange={(e) => setAnyDriver(e.target.checked)}
            />
            {t('AnyDriver') || 'Any assigned driver'}
          </label>
          {!anyDriver && (
            <SelectUsers
              selectedTasks={driverIds}
              setSelectedTasks={setDriverIds}
            />
          )}
        </div>

        {/* Document */}
        <div className="fx-field">
          <label className="fx-dropzone">
            <div className="fx-dz-icon"><UploadIcon fontSize="small" /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="fx-dz-main truncate">
                {file ? file.name : (docUrl ? (t('CurrentDocument') || 'Current document') : (t('BrowseDocument') || 'Upload document'))}
              </div>
              <div className="fx-dz-sub">PDF</div>
            </div>
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          {!file && docUrl && (
            <a
              href={`${baseUrl}${docUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fx-dz-sub underline"
              style={{ marginTop: 4 }}
            >
              📄 {t('CurrentDocument') || 'Current document'}
            </a>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="fx-foot">
        <button type="button" className="fx-save" disabled={saving} onClick={handleSave}>
          {isEdit ? t('Save') || 'Save' : t('Add') || 'Add'}
        </button>
        <button type="button" className="fx-cancel" onClick={onClose}>
          {t('Cancel') || 'Cancel'}
        </button>
      </div>
    </div>
  );
};

export default InsuranceForm;
